import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Col, Row} from "reactstrap";
import {ReactComponent as Printer} from '../../../../../../../assets/images/printer.svg'
import {formatMoney, stringComparator} from "../../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Tab, Tabs} from "../../../../../../components/base/Tabs";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ArrowBack, FileCopyOutlined, MailOutline} from "@mui/icons-material";
import {Box, SvgIcon, Tooltip, Typography} from "@mui/material";
import Api from "../../../../../../../core/services/api/api";
import ToastService from "../../../../../../../core/services/toast";
import OrderInformationViewTransactionsSection, {OrderInformationViewTransactionsTypes} from "../../../../../../components/app-specific/order-inforamtion-shared-sections/transactions";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import {Popover} from "../../../../../../components/app-specific/popover";
import Button from "../../../../../../components/base/button/button";
import SendOrderEmailDialog from "../../../../../../components/dialogs/send-order-email";
import OrderInformationViewNotesSection, {OrderNoteAutoDefineTypes} from "../../../../../../components/app-specific/order-inforamtion-shared-sections/notes";
import OrderInformationViewShippingInfoCard from "../../../../../../components/app-specific/order-inforamtion-shared-sections/shipping-info-card/shipping-information";
import OrderInformationViewReadOnlyAddressesSection from "../../../../../../components/app-specific/order-inforamtion-shared-sections/read-only-addresses";
import SummaryInfoCard from "../../../../../../components/app-specific/summary-cards/summary-info-card";
import InvoiceInformationViewBillInfoSection from "./sections/bill-info";
import InvoiceInformationViewProductsSection from "./sections/products";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../../core/services/date-utils";
import useCachedPriceLevelsDictionaryController from "../../../../../../hooks/use-price-level-dictionary";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {Link, useParams} from "react-router-dom";
import {useAppSelector} from "../../../../../../redux";
import useQuery from "../../../../../../hooks/use-query";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import PrintOrderConfigurationsDialog from "../../../../../../components/dialogs/print-order-configurations";
import {stringifyUrl} from "query-string";
import UpsertOrderInfoDialog from "../../../../../../components/dialogs/upsert-order-info";
import OrderDimensionsSection from "../../../../../../components/app-specific/order-inforamtion-shared-sections/dimensions";
import DuplicateOrderDialog, {DuplicateOrderVariants} from "../../../../../../components/dialogs/duplicate-order-dialog";

const tabs = {
    products: 'products',
    addresses: 'addresses',
    transactions: 'transactions',
    notes: 'notes',
    dimensions: 'dimensions',
}

const orderInfoSummaryBodyIds = {
    transferCode: 'transferCode',
    orderNo: 'orderNo',
    termsCode: 'termsCode',
    salesPerson: 'salesPerson',
    accountManager: 'accountManager',
    territory: 'territory',
    poNo: 'poNo',
    orderDate: 'orderDate',
    requiredDate: 'requiredDate',
    invoiceDate: 'invoiceDate',
    referenceNo: 'referenceNo',
}

const userInfoSummaryBodyIds = {
    accountReceivable: 'accountReceivable',
    limitedCredit: 'limitedCredit',
    otherOrders: 'otherOrders',
    availableCredit: 'availableCredit',
    priceLevel: 'priceLevel',
    paymentTerms: 'paymentTerms',
}

const defaultTab = tabs.products

const initSendOrderEmailDialogState = {open: false, multi: false};
const initPrintOrderConfigurationsDialogState = {open: false, printUrl: '', onClose: () => void 0};
const initUpsertOrderInfoDialogState = {open: false, entityToShow: ''};

const InvoiceInformationView = ({allowPayments}) => {
    const params = useParams();
    const [query, setQuery] = useQuery();
    const [loading, setLoading] = useState(true);
    const [order, _setOrder] = useState({});
    const [accountInfoLoading, setAccountInfoLoading] = useState(true);
    const [accountInfo, setAccountInfo] = useState({});
    const [sendOrderEmailDialogState, setSendOrderEmailDialogState] = useState(initSendOrderEmailDialogState);
    const [shippingInfo, setShippingInfo] = useState({});
    const [freeShippingEligibility, setFreeShippingEligibility] = useState({});
    const [freightEstimation, setFreightEstimation] = useState(null);
    const [loadingFreightEstimation, setLoadingFreightEstimation] = useState(false);
    const [loadingShippingInfo, setLoadingShippingInfo] = useState(true);
    const [printOrderConfigurationsDialog, setPrintOrderConfigurationsDialog] = useState(initPrintOrderConfigurationsDialogState);
    const [upsertOrderInfoDialog, setUpsertOrderInfoDialog] = useState(initUpsertOrderInfoDialogState);
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const isMounted = useIsMounted();
    const freeFreightRequestIdRef = useRef(null);
    const shippingInfoCompanyIdRef = useRef(null);
    const salesOrderIdRef = useRef(null);

    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const {configureNotebookTags} = useConfigureNotebook('invoice-information-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();
    const operatorAccessLevels = useAccessLevels();

    const tabsList = useMemo(() => {
        const res = [
            {label: "Products", value: tabs.products},
            {label: "Addresses", value: tabs.addresses},
            {label: "Transactions", value: tabs.transactions},
            {label: "Notes", value: tabs.notes},
            {label: "Dimensions", value: tabs.dimensions},
        ]
        if (!operatorAccessLevels.packages.sales.salesPallets) {
            res.splice(res.findIndex(e => e.value === tabs.dimensions), 1);
        }
        return res;
    }, [operatorAccessLevels.packages.sales.salesPallets])

    const orderId = useMemo(() => Number(params.orderId), [params.orderId])
    const customerId = useMemo(() => Number(params.customerId), [params.customerId])

    const tab = useMemo(() => {
        let selected = query?.tab;
        if (!selected || !tabsList?.map(e => e.value).includes(selected)) {
            selected = defaultTab;
        }
        return selected;
    }, [query?.tab, tabsList])

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool
     */
    useEffect(() => {
        if (!defaultCustomer?.code)
            return;
        if (order?.orderId) {
            configureNotebookTags(
                (tags) => tags.customerSalesInvoiceInformation(defaultCustomer?.code, orderId),
                (tags) => tags.customerSalesSalesOrderInformation(defaultCustomer?.code, order?.orderId),
                (tags) => tags.customerSalesQuoteInformation(defaultCustomer?.code, order?.orderId),
            );
            configureBehavioralTool(tags => tags.customerSalesInvoiceInformation(defaultCustomer?.code, orderId));
        } else {
            configureNotebookTags((tags) => tags.customerSalesInvoiceInformation(defaultCustomer?.code, orderId));
            configureBehavioralTool(tags => tags.customerSalesInvoiceInformation(defaultCustomer?.code, orderId));
        }
    }, [order?.orderId, orderId, defaultCustomer?.code, configureNotebookTags, configureBehavioralTool]);


    /**
     * Sets the order state with its transactions sorted.
     * @type {function(*=): void}
     */
    const setOrder = useCallback((stateOrFunctionWithPrevState) => {
        _setOrder(prevState => {
            const newState = typeof stateOrFunctionWithPrevState === "function" ? stateOrFunctionWithPrevState(prevState) : stateOrFunctionWithPrevState;
            // sort the appliedTransactions by their type of the id so that the gift card can be at the beginning
            // or at the end.
            newState.appliedTransactions?.sort((a, b) => stringComparator(a.transactionTypeName, b.transactionTypeName));
            return {...newState}
        });
    }, [])

    /**
     * Fetches the customer account information from the server.
     * @type {function(): Promise<void>}
     */
    const getCustomerAccountInformation = useCallback(async () => {
        setAccountInfoLoading(true);
        const [response, priceLevelDict] = await Promise.all([
            Api.getCustomerAccountInformation(customerId),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setAccountInfo({
                ...response.data,
                priceLevel: {
                    ...response.data.priceLevel,
                    title: priceLevelDict?.find(e => e.priceLevelId === response.data.priceLevel.id)?.title ?? response.data.priceLevel.title,
                },
            })
        }
        setAccountInfoLoading(false);
    }, [getPriceLevelDictionary, isMounted, customerId])

    /**
     * As soon as the component is mounted:
     * - fetches user account information from the server.
     */
    useEffect(() => {
        void getCustomerAccountInformation();
    }, [getCustomerAccountInformation])

    /**
     * Fetches the free shipping eligibility of the order from the server.
     * @type {function(*=): Promise<void>}
     */
    const getFreeShippingEligibility = useCallback(async (freeFreightId = undefined) => {
        if (!operatorAccessLevels.packages.sales.orderFreightInfo) {
            return;
        }
        const response = await Api.getFreeFreightRequest(freeFreightId ?? freeFreightRequestIdRef.current);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setFreeShippingEligibility(response.data);
            setShippingInfo(prevState => ({...prevState, freeFreightId: response.data.id}));
        } else {
            setFreeShippingEligibility({});
        }
    }, [isMounted, operatorAccessLevels.packages.sales.orderFreightInfo])

    /**
     * Fetches the estimated freight fee for the invoice based on freight company and shipping address.
     * @type {function(number=): Promise<void>}
     */
    const getFreightEstimation = useCallback(async (freightCompanyId = undefined) => {
        if (!operatorAccessLevels.packages.sales.orderFreightInfo)
            return;
        const resolvedFreightCompanyId = freightCompanyId ?? shippingInfoCompanyIdRef.current;
        const salesOrderId = salesOrderIdRef.current;
        if (!resolvedFreightCompanyId || !salesOrderId) {
            setFreightEstimation(null);
            return;
        }
        setLoadingFreightEstimation(true);
        const response = await Api.estimateShippingFee({
            orderId: salesOrderId,
            freightCompanyId: resolvedFreightCompanyId,
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setFreightEstimation(response.data);
        } else {
            setFreightEstimation(null);
        }
        setLoadingFreightEstimation(false);
    }, [isMounted, operatorAccessLevels.packages.sales.orderFreightInfo])

    /**
     * Fetches the order's shipping information from the server.
     * @type {function(*=): Promise<void>}
     */
    const getOrderShippingInformation = useCallback(async (orderInfo) => {
        if (!operatorAccessLevels.packages.sales.orderFreightInfo) {
            setLoadingShippingInfo(false);
            return;
        }
        setLoadingShippingInfo(true);
        const response = await Api.getFreightOrderInformation({invoiceId: orderId});
        if (!isMounted())
            return;
        setShippingInfo({
            id: response?.data?.id,
            freeFreightId: response?.data?.freeFreightRequestId,
            freightCompany: {
                id: response?.data?.freightCompanyId,
                title: response?.data?.freightCompanyTitle,
                code: response?.data?.freightCompanyCode ?? orderInfo?.freightCompanyCode
            },
            estimatedDate: response?.data?.estimatedDeliveryDateTime ?? orderInfo?.shipDate,
            estimatedFee: response?.data?.estimatedFee,
            customerFee: response?.data?.customerFee ?? orderInfo?.freightFee,
            trackingNo: response?.data?.trackingNumber ?? orderInfo?.trackingNo,
            shippingBillTo: response?.data?.shippingBillTo ?? null,
            metaData: response?.data?.metaData ?? null,
        });
        freeFreightRequestIdRef.current = response?.data?.freeFreightRequestId;
        shippingInfoCompanyIdRef.current = response?.data?.freightCompanyId;
        salesOrderIdRef.current = orderInfo?.orderId;
        if (response?.resultFlag && response.data?.freightCompanyId && orderInfo?.orderId) {
            await Promise.all([
                getFreeShippingEligibility(response.data.freeFreightRequestId),
                getFreightEstimation(response.data.freightCompanyId),
            ]);
        } else {
            setFreightEstimation(null);
        }
        setLoadingShippingInfo(false);
    }, [orderId, isMounted, getFreeShippingEligibility, getFreightEstimation, operatorAccessLevels.packages.sales.orderFreightInfo])


    /**
     * Fetches the full information of the current order from the server.
     * - if the response is successful, sets the order in the state
     * @type {function(*=): Promise<void>}
     */
    const getOrderInformation = useCallback(async (setLoadingState = false) => {
        if (setLoadingState)
            setLoading(true);
        const response = await Api.getInvoiceInformationForCustomer(customerId, orderId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        setLoading(false);
        void getOrderShippingInformation(response?.data);
    }, [customerId, orderId, isMounted, getOrderShippingInformation, setOrder])

    /**
     * With each change in the [customerId] or the [orderId] of the url params:
     * - fetches user account information and order information from the server.
     */
    useEffect(() => {
        void getOrderInformation();
    }, [getOrderInformation])

    /**
     * Removes the selected transaction from the order
     *
     * - if the result of the api is successful, removes the transaction from transactions list of the order.
     * @type {function(*=): Promise<void>}
     */
    const removeTransactionFromOrder = useCallback(async (transactionId) => {
        const numericTransactionId = Number(transactionId);
        if (!Number.isFinite(numericTransactionId)) {
            ToastService.error({
                title: 'Refund failed',
                message: 'The transaction could not be refunded because its identifier is invalid.',
            });
            return;
        }
        const forApi = {
            transactionId: numericTransactionId,
            orderId: orderId,
            customerId: customerId,
        }
        const response = await Api.removeTransactionItemFromInvoice(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOrder(response.data);
        }
    }, [orderId, customerId, isMounted, setOrder])

    /**
     * Sends an email of this order's information to the provided email address of the user by calling its appropriate
     * api.
     * @type {function(*=, *=, *=): Promise<boolean>}
     */
    const sendEmail = useCallback(async (emailAddress, includeBCC, multiInvoices = false) => {
        const forApi = {
            customerId: customerId,
            orderId: orderId,
            sendBccToOperator: includeBCC,
            email: emailAddress,
        };
        let response;
        if (!multiInvoices) {
            response = await Api.sendInvoiceInformationToUserEmail(forApi);
        } else {
            response = await Api.sendMultiInvoiceInformationToUserEmail(forApi);
        }
        return response?.resultFlag;
    }, [customerId, orderId])

    /**
     * Pays for a current order with the provided values.
     * @type {function(*=): Promise<[boolean, {send: sendEmail, open: boolean}, function(): void]>}
     */
    const payOrder = useCallback(async (values) => {
        let forApi = {
            ...values,
            customerId: customerId,
            orderId: orderId
        };
        const response = await Api.payInvoice(forApi);
        if (!isMounted())
            return [];
        const sendEmailConfirmation = {
            send: sendEmail,
        };
        if (response?.resultFlag) {
            setOrder(response.data);
        }
        return [
            response?.resultFlag ?? false,
            sendEmailConfirmation,
        ]
    }, [customerId, orderId, isMounted, sendEmail, setOrder])

    /**
     * Updates the tracking no of the shipping information of this order.
     * @type {function(*=): Promise<boolean>}
     */
    const updateShippingInfoTrackingNo = useCallback(async (trackingNo) => {
        if (!operatorAccessLevels.packages.sales.manageInvoices || !operatorAccessLevels.packages.sales.orderFreightInfo) {
            return;
        }
        const forApi = {
            trackingNo: trackingNo,
            invoiceId: orderId,
            freightInfoId: shippingInfo?.id,
            shippingBillTo: shippingInfo?.shippingBillTo ?? null,
            metaData: shippingInfo?.metaData ?? null,
        }
        const response = await Api.saveInvoiceFreightInfo(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setShippingInfo(prevState => ({
                ...prevState,
                trackingNo: trackingNo,
            }))
        }
        return response?.resultFlag;
    }, [orderId, shippingInfo?.id, shippingInfo?.shippingBillTo, shippingInfo?.metaData, isMounted, operatorAccessLevels.packages.sales.manageInvoices, operatorAccessLevels.packages.sales.orderFreightInfo])

    /**
     * Changes the tab of the view.
     * @type {function(*=, *=): void}
     */
    const onTabChange = useCallback((_, newValue) => {
        setQuery(prevState => ({...prevState, tab: newValue}));
    }, [setQuery]);

    /**
     * Opens the send-email-dialog.
     * @param {boolean} multi whether to attach all the invoice transactions by including all of its related
     * invoices.
     * @type {function(*=): void}
     */
    const openEmailConfirmation = useCallback((multi = false) => {
        setSendOrderEmailDialogState({
            open: true,
            multi: multi,
        });
    }, [])

    /**
     * Updates the order's information.
     *
     * - if the result of the api is successful, sets the new information.
     * @param {any} entry
     * @param {string} newValue
     * @return {boolean}
     */
    const updateOrderInfo = useCallback(async (entry, newValue) => {
        let name;
        switch (entry.id) {
            case orderInfoSummaryBodyIds.poNo:
                name = 'customerPO';
                break;
            case orderInfoSummaryBodyIds.referenceNo:
                name = 'referenceNo';
                break;
            case orderInfoSummaryBodyIds.territory: {
                name = 'territory';
                let abort = false;
                [newValue, abort] = await new Promise(resolve => {
                    setUpsertOrderInfoDialog({
                        open: true,
                        entityToShow: 'territory',
                        territory: order.territory,
                        close: () => {
                            resolve([undefined, true])
                            setUpsertOrderInfoDialog(initUpsertOrderInfoDialogState)
                        },
                        update: (values) => {
                            resolve([values.territory, false])
                        },
                    })
                })
                if (abort)
                    return true;
                break;
            }
            case orderInfoSummaryBodyIds.accountManager: {
                name = 'accountManagerNo';
                let abort = false;
                [newValue, abort] = await new Promise(resolve => {
                    setUpsertOrderInfoDialog({
                        open: true,
                        entityToShow: 'accountManager',
                        accountManager: order.accountManagerNo ? {code: order.accountManagerNo} : null,
                        close: () => {
                            resolve([undefined, true])
                            setUpsertOrderInfoDialog(initUpsertOrderInfoDialogState)
                        },
                        update: (values) => resolve([values.accountManager?.code, false]),
                    })
                })
                if (abort)
                    return true;
                break;
            }
            case orderInfoSummaryBodyIds.termsCode: {
                name = 'paymentTermsCode';
                let abort = false;
                [newValue, abort] = await new Promise(resolve => {
                    setUpsertOrderInfoDialog({
                        open: true,
                        entityToShow: 'termsCode',
                        termsCode: order.paymentTermsCode ?? order.termsCode,
                        close: () => {
                            resolve([undefined, true])
                            setUpsertOrderInfoDialog(initUpsertOrderInfoDialogState)
                        },
                        update: (values) => resolve([values.termsCode, false]),
                    })
                })
                if (abort)
                    return true;
                break;
            }
            default:
                return;
        }
        const forApi = {
            customerId: customerId,
            invoiceId: orderId,
            customerPo: entry.id === orderInfoSummaryBodyIds.poNo ? newValue ?? '' : order.customerPO,
            referenceNo: entry.id === orderInfoSummaryBodyIds.referenceNo ? newValue ?? '' : order.referenceNo,
            territoryCode: entry.id === orderInfoSummaryBodyIds.territory ? newValue?.code ?? '' : order.territory?.code,
            accountManagerCode: entry.id === orderInfoSummaryBodyIds.accountManager ? newValue ?? null : order.accountManagerNo,
            paymentTerms: entry.id === orderInfoSummaryBodyIds.termsCode ? newValue ?? '' : (order.paymentTermsCode ?? order.termsCode),
        };
        const response = await Api.updateInvoicePoReference(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setOrder(prevState => ({
                ...prevState,
                [name]: newValue,
                ...(entry.id === orderInfoSummaryBodyIds.termsCode && {termsCode: newValue}),
            }));
            if (entry.id === orderInfoSummaryBodyIds.termsCode) {
                // fetch the order silently to update the general information.
                void getOrderInformation();
            }
        }
        return response?.resultFlag;
    }, [customerId, orderId, order.customerPO, order.referenceNo, order.territory, order.accountManagerNo, order.paymentTermsCode, order.termsCode, isMounted, setOrder, getOrderInformation])


    const orderInfoSummary = useMemo(() => ({
        header: {
            title: 'Invoice No',
            value: order?.invoiceNo,
            loadingValue: "Loading...",
            copyable: true,
        },
        body: [
            {
                id: orderInfoSummaryBodyIds.transferCode,
                title: 'Transfer code',
                value: order?.transNo,
                copyable: true,
            },
            {
                id: orderInfoSummaryBodyIds.orderNo,
                title: 'Order no',
                value: order?.orderNo,
                copyable: true,
            },
            {
                id: orderInfoSummaryBodyIds.termsCode,
                title: 'Payment Terms',
                value: order?.paymentTermsCode ?? order?.termsCode,
                editable: false, // invoices must not change their payment terms.
                edit: updateOrderInfo,
                openDialog: true,
            },
            {
                id: orderInfoSummaryBodyIds.salesPerson,
                title: 'Sales person',
                value: order?.salespersonNo,
            },
            {
                id: orderInfoSummaryBodyIds.accountManager,
                title: 'Account Manager',
                value: order?.accountManagerNo,
                editable: !!order && operatorAccessLevels.packages.base.manageTerritories,
                clearable: !!order,
                edit: updateOrderInfo,
                openDialog: true,
            },
            {
                id: orderInfoSummaryBodyIds.territory,
                title: 'Territory',
                value: order?.territory?.code,
                editable: !!order && operatorAccessLevels.packages.base.manageTerritories,
                clearable: !!order,
                edit: updateOrderInfo,
                openDialog: true,
            },
            {
                id: orderInfoSummaryBodyIds.orderDate,
                title: 'Order date',
                value: order?.orderDate,
                format: (val) => val ? DateUtils.serverToLocal(val).format(DateUtils.dateFormat) : '--',
            },
            {
                id: orderInfoSummaryBodyIds.requiredDate,
                title: 'Required date',
                value: order?.requiredDate,
                format: (val) => val ? DateUtils.serverToLocal(val).format(DateUtils.dateFormat) : '--',
            },
            {
                id: orderInfoSummaryBodyIds.invoiceDate,
                title: 'Invoice date',
                value: order?.invoiceDate,
                format: (val) => val ? DateUtils.serverToLocal(val).format(DateUtils.dateFormat) : '--',
            },
            {
                id: orderInfoSummaryBodyIds.poNo,
                title: 'Po number',
                value: order?.customerPO,
                editable: !!order && operatorAccessLevels.packages.sales.manageInvoices,
                clearable: !!order,
                edit: updateOrderInfo,
            },
            {
                id: orderInfoSummaryBodyIds.referenceNo,
                title: 'Reference no',
                value: order?.referenceNo,
                editable: !!order && operatorAccessLevels.packages.sales.manageInvoices,
                clearable: !!order && operatorAccessLevels.packages.sales.manageInvoices,
                edit: updateOrderInfo,
            },
        ],
    }), [order, operatorAccessLevels.packages.base.manageTerritories, operatorAccessLevels.packages.sales.manageInvoices, updateOrderInfo])

    const userInfoSummary = useMemo(() => ({
        header: {
            title: 'Customer',
            value: accountInfo?.customer?.name,
            loadingValue: "Loading...",
        },
        body: [
            {
                id: userInfoSummaryBodyIds.accountReceivable,
                title: 'Account receivable',
                value: accountInfo?.accountReceivable,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.limitedCredit,
                title: 'Credit limit',
                value: accountInfo?.creditLimit,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.otherOrders,
                title: 'Other orders',
                value: accountInfo?.otherOrders,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.availableCredit,
                title: 'Available credit',
                value: accountInfo?.availableCredit,
                format: formatMoney,
            },
            {
                id: userInfoSummaryBodyIds.priceLevel,
                title: 'Price level',
                value: accountInfo?.priceLevel?.title,
            },
            {
                id: userInfoSummaryBodyIds.paymentTerms,
                title: 'Payment terms',
                value: accountInfo?.paymentTermsCode,
            },
        ],
    }), [accountInfo])

    /**
     * Opens the print configuration dialog.
     * @type {(function(): void)}
     */
    const openPrintConfigDialog = useCallback(() => {
        setPrintOrderConfigurationsDialog({
            open: true,
            printUrl: routeFunctions.print.invoice(customerId, orderId),
            onClose: () => setPrintOrderConfigurationsDialog(initPrintOrderConfigurationsDialogState)
        })
    }, [customerId, orderId])

    return (
        <>
            <div className='order-information-view sales-views-top-progress-indicator-offset'>
                <Row className='no-gutters w-100 align-items-center justify-content-between'>
                    <Row className='justify-content-start align-items-center no-gutters'>
                        <Tooltip title={`Back to ${defaultCustomer?.name}'s invoices`}>
                            <Box component={'span'}>
                                <IconButton
                                    color={'paper.secondary'}
                                    component={Link}
                                    to={allowPayments ? routeFunctions.customer.accounting.invoices.base(customerId) : routeFunctions.customer.sales.invoices.base(customerId)}
                                    replace
                                >
                                    <ArrowBack className='icon normal'/>
                                </IconButton>
                            </Box>
                        </Tooltip>
                        <Typography variant={'h6'} fontWeight={600} marginInlineStart={1}>
                            Invoice Information
                        </Typography>
                    </Row>
                    <Row className='justify-content-end align-items-center no-gutters' style={{gap: 8}}>
                        {
                            operatorAccessLevels.packages.sales.createOrderFromAnother &&
                            <Tooltip arrow title={'Create Order from this Invoice'}>
                                <IconButton
                                    onPressed={() => setDuplicateDialogOpen(true)}
                                    color={'paper.secondary'}
                                >
                                    <FileCopyOutlined className={'icon large'}/>
                                </IconButton>
                            </Tooltip>
                        }
                        <Popover
                            child={(onEnter) => (
                                <Tooltip arrow title={'Send order information in an email'}>
                                    <IconButton
                                        onPressed={onEnter}
                                        color={'paper.secondary'}
                                        className={'email-icon-container'}
                                    >
                                        <MailOutline className='icon large'/>
                                    </IconButton>
                                </Tooltip>
                            )}
                            popoverChild={() => (
                                <Row
                                    className='no-gutters align-items-center justify-content-center px-2'
                                    style={{maxWidth: '15rem'}}>
                                    <Col xs={12} className={'mx-2 my-2'}>
                                        <Button
                                            onClick={() => openEmailConfirmation(false)}
                                            color={'secondary'}
                                            variant={'text'}
                                            className={'w-100'}
                                        >
                                            Without transactions
                                        </Button>
                                    </Col>
                                    <Col xs={12} className={'mx-2 mb-2'}>
                                        <Button
                                            onClick={() => openEmailConfirmation(true)}
                                            color={'secondary'}
                                            variant={'text'}
                                            className={'w-100'}
                                        >
                                            With transactions
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        />
                        {
                            operatorAccessLevels.packages.bizPrint.printInvoice &&
                            <Popover
                                child={(onEnter) => (
                                    <Tooltip arrow title={'Print Invoice'}>
                                        <IconButton
                                            onPressed={onEnter}
                                            color={'paper.secondary'}
                                        >
                                            <SvgIcon className='icon large' component={Printer}/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                                popoverChild={() => (
                                    <Row
                                        className='no-gutters align-items-center justify-content-center px-2'
                                        style={{maxWidth: '13rem'}}>
                                        <Col xs={12} className={'mx-2 my-2'}>
                                            <Button
                                                color={'secondary'}
                                                variant={'text'}
                                                className={'w-100'}
                                                component={Link}
                                                target={'_blank'}
                                                to={stringifyUrl({
                                                    url: routeFunctions.print.invoice(params.customerId, params.orderId),
                                                    query: {showAddresses: true, showPaymentTerms: true, showPrices: true, showTransactions: false}
                                                })}
                                            >
                                                Without transactions
                                            </Button>
                                        </Col>
                                        <Col xs={12} className={'mx-2 mb-2'}>
                                            <Button
                                                color={'secondary'}
                                                variant={'text'}
                                                className={'w-100'}
                                                component={Link}
                                                target={'_blank'}
                                                to={stringifyUrl({
                                                    url: routeFunctions.print.invoice(params.customerId, params.orderId),
                                                    query: {showAddresses: true, showPaymentTerms: true, showPrices: true, showTransactions: true}
                                                })}
                                            >
                                                With transactions
                                            </Button>
                                        </Col>
                                        <Col xs={12} className={'mx-2 mb-2'}>
                                            <Button
                                                color={'secondary'}
                                                variant={'text'}
                                                className={'w-100'}
                                                onClick={openPrintConfigDialog}
                                            >
                                                Custom
                                            </Button>
                                        </Col>
                                    </Row>
                                )}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            />
                        }
                    </Row>
                </Row>
                <div className='w-100 my-3'/>
                <div className='d-flex flex-wrap w-100 justify-content-start'>
                    <Col xs={12} md={6} xl={4} className={'mb-3'}>
                        <SummaryInfoCard
                            header={orderInfoSummary.header}
                            body={orderInfoSummary.body}
                            loading={loading}
                            loadingCount={orderInfoSummary.body.length}
                        />
                    </Col>
                    <Col xs={12} md={6} xl={4} className={'mb-3'}>
                        <SummaryInfoCard
                            header={userInfoSummary.header}
                            body={userInfoSummary.body}
                            loading={accountInfoLoading}
                            loadingCount={userInfoSummary.body.length}
                        />
                    </Col>
                    <Col xs={12} md={6} xl={4} className={'mb-3'}>
                        <InvoiceInformationViewBillInfoSection
                            order={order}
                            setOrder={setOrder}
                            loading={loading}
                            customerId={customerId}
                            payOrder={payOrder}
                            allowPayments={allowPayments}
                            removeTransaction={removeTransactionFromOrder}
                        />
                    </Col>
                    {
                        operatorAccessLevels.packages.sales.orderFreightInfo &&
                        <Col xs={12} md={6} xl={8} className={'mb-3'}>
                            <OrderInformationViewShippingInfoCard
                                viewOnly
                                loading={loading || loadingShippingInfo}
                                freeShippingEligibility={freeShippingEligibility}
                                shippingInfo={shippingInfo}
                                freightEstimation={freightEstimation}
                                loadingFreightEstimation={loadingFreightEstimation}
                                updateTrackingNo={operatorAccessLevels.packages.sales.manageInvoices
                                    ? updateShippingInfoTrackingNo
                                    : undefined}
                                getFreeShippingEligibility={getFreeShippingEligibility}
                                getFreightEstimation={getFreightEstimation}
                                customerId={customerId}
                            />
                        </Col>
                    }
                </div>
                <div className='w-100 my-4'/>
                <div className='w-100 flex-grow-1'>
                    <Tabs value={tab} onChange={onTabChange}>
                        {
                            tabsList.map(tab => (
                                <Tab
                                    key={tab.value}
                                    label={tab.label}
                                    value={tab.value}
                                />
                            ))
                        }
                    </Tabs>
                </div>
                <div className='my-3 w-100'/>
                {
                    tabs.products === tab &&
                    <InvoiceInformationViewProductsSection
                        order={order}
                        setOrder={setOrder}
                        orderLoading={loading}
                    />
                }
                {
                    tabs.addresses === tab &&
                    <OrderInformationViewReadOnlyAddressesSection
                        billing={order?.billingAddress}
                        shipping={order?.shippingAddress}
                        loading={loading}
                    />
                }
                {
                    tabs.transactions === tab &&
                    <OrderInformationViewTransactionsSection
                        appliedTransactions={order?.appliedTransactions}
                        loading={loading}
                        orderId={orderId}
                        customerId={customerId}
                        type={OrderInformationViewTransactionsTypes.invoice}
                    />
                }
                {
                    tabs.notes === tab &&
                    <OrderInformationViewNotesSection
                        orderNo={order?.orderNo}
                        loading={loading}
                        placeType={OrderNoteAutoDefineTypes.invoice}
                        editable={operatorAccessLevels.packages.sales.manageInvoices}
                    />
                }
                {
                    tab === tabs.dimensions && operatorAccessLevels.packages.sales.salesPallets &&
                    <OrderDimensionsSection
                        invoiceId={orderId}
                        loading={loading}
                        items={order?.items}
                    />
                }
                <div className='my-3 w-100'/>
            </div>
            <SendOrderEmailDialog
                open={sendOrderEmailDialogState.open}
                close={() => setSendOrderEmailDialogState(initSendOrderEmailDialogState)}
                send={(emailAddress, includeBCC) => sendEmail(emailAddress, includeBCC, sendOrderEmailDialogState.multi)}
                customerId={customerId}
            />
            <PrintOrderConfigurationsDialog
                open={printOrderConfigurationsDialog.open}
                onClose={printOrderConfigurationsDialog.onClose}
                printUrl={printOrderConfigurationsDialog.printUrl}
            />
            <UpsertOrderInfoDialog
                open={upsertOrderInfoDialog.open}
                close={upsertOrderInfoDialog.close}
                entityToShow={upsertOrderInfoDialog.entityToShow}
                update={upsertOrderInfoDialog.update}
                salesPerson={upsertOrderInfoDialog.salesPerson}
                territory={upsertOrderInfoDialog.territory}
                accountManager={upsertOrderInfoDialog.accountManager}
                termsCode={upsertOrderInfoDialog.termsCode}
            />
            {
                operatorAccessLevels.packages.sales.createOrderFromAnother &&
                <DuplicateOrderDialog
                    recordId={orderId}
                    customerId={customerId}
                    variant={DuplicateOrderVariants.Invoices}
                    open={duplicateDialogOpen}
                    close={() => setDuplicateDialogOpen(false)}
                />
            }
        </>
    );
}

export default InvoiceInformationView;
